<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal :visible="vendorChargeModal.show"
                   :data="model"
                   :title="!vendorChargeModal.object ? $t(`Create New Charge`) : vendorChargeModal.object.level == source ? $t(`Update Charge`) : $t(`Override Charge ({level})`, {level: vendorChargeModal.object.level})"
                   @show="onShow"
                   @hidden="onHidden"
                   @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')"
                   :is-loading="loading"
                   scrollable
                   :size="computedSize">
      <div class="row">
        <div v-if="hasPredefinedVendorChargeNames"
          :class="{'col-md-6': model.name_dropdown_value === 'other', 'col-md-12': model.name_dropdown_value !== 'other'}">
          <input-widget :items="predefinedVendorChargeNames" value-field="key" display-field="${item.value}"
                        @select2Select="onChargeNameDropdownValueChange()"
                        type="select2" :model="model" attribute="name_dropdown_value"
                        :disabled="model.level && !!model.data.func_name || model.level && model.level != source"/>
        </div>
        <div v-if="!hasPredefinedVendorChargeNames || model.name_dropdown_value === 'other'"
          :class="{'col-md-12': !hasPredefinedVendorChargeNames, 'col-md-6': model.name_dropdown_value === 'other'}">
          <input-widget v-if="!hasPredefinedVendorChargeNames" :model="model" attribute="name"
            :rules="'required'"
            :disabled="model.level && !!model.data.func_name || model.level && model.level != source"/>
          <input-widget v-else :model="model" attribute="name" :label="false" style="margin-top: 28px;"
            :rules="model.name_dropdown_value === 'other' ? 'required' : ''"
            :disabled="model.level && !!model.data.func_name || model.level && model.level != source"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <input-widget :model="model.data" attribute="comment"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card mb-3">
            <div class="card-header" v-if="model.data.func_name">
              <span>{{ $t("Function") }}: <span class="bold">{{ model.data.func_name }}</span></span>
            </div>
            <div class="card-body">
              <div class="row">
                <div :class="{'col-md-12': model.data.func_name, 'col-md-6': !model.data.func_name}">
                  <input-widget :model="model.data" attribute="applies_to" type="select2" :items="calculatedAppliesTo"
                        display-field="${item.key}" value-field="value"
                        :disabled="model.is_overridden || !!model.data.func_name || model.level && model.level != source"/>
                </div>
                <div class="col-md-6" v-if="model.data.applies_to === 'lot' && !model.data.func_name">
                  <input-widget :model="model.data" attribute="lot_statuses" type="select2"
                    :items="vendorChargeLotStatuses" display-field="${item.key}" value-field="value" multiple
                    :rules="model.data.applies_to === 'lot' && !model.data.func_name? 'required' : ''"/>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6" v-if="!model.data.func_name">
                  <input-widget type="checkbox" :model="model.data" attribute="is_aggregate" is-switch
                      :disabled="!percentageUsed"/>
                </div>
                <div class="col-md-6">
                  <input-widget type="checkbox" :model="model.data" attribute="is_active" is-switch/>
                </div>
                <div class="col-md-6" v-if="model.data.func_name && model.data.applies_to === 'lot'">
                  <input-widget type="checkbox" :model="model.data" attribute="is_grouped" is-switch disabled/>
                </div>
              </div>
              <div class="row" v-if="!model.data.func_name">
                <div class="col-md-6">
                  <input-widget type="checkbox" :model="model.data" attribute="is_taxable" is-switch/>
                  <input-widget type="checkbox" :model="model.data" attribute="is_tax_inclusive" is-switch
                    :disabled="!model.data.is_taxable"/>
                </div>
                <div class="col-md-6">
                  <percentage-input-widget :model="model.data" attribute="tax_rate" :disabled="!model.data.is_taxable"/>
                </div>
              </div>
              <div class="row" v-if="!model.data.func_name">
                <div class="col-md-6">
                  <input-widget :model="model.data" attribute="calculate_from" type="select2" :items="calculateFrom"
                        display-field="${item.key}" value-field="value" multiple :disabled="!percentageUsed"
                        :rules="percentageUsed ? 'required' : ''"/>
                </div>
                <div class="col-md-6">
                  <input-widget v-if="model.data.calculate_from.length" type="number" :model="model.data"
                        attribute="minimum_calculate_from"/>
                </div>
              </div>
              <div class="row" v-if="!model.data.func_name">
                <div class="col-md-12">
                  <div class="card mb-3">
                    <div class="card-header">
                      {{ $t("Tiers") }}
                    </div>
                    <div class="card-body">
                      <vendor-charge-tiers :model="model.data" @update-percentage-used="handleTierChange"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import VendorChargeModel from "./VendorCharge.model";
  import {createNamespacedHelpers} from "vuex";
  import VendorChargesService from "./VendorChargesService";
  import VendorChargeTiers from "@/core/components/vendor-charge-tiers/VendorChargeTiers";
  import cloneDeep from 'lodash/cloneDeep';
  import PercentageInputWidget from "@/core/widgets/PercentageInputWidget";
  import i18n from '@/shared/i18n';

  const {mapState, mapActions} = createNamespacedHelpers('vendorCharges');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "VendorChargeModal",
    components: {InputWidget, ArtisioModal, VendorChargeTiers, PercentageInputWidget},
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new VendorChargeModel(),
        percentageUsed: false,
      }
    },
    computed: {
      ...mapState({
        vendorChargeModal: state => state.vendorChargeModal,
        tableData: state => state.tableData[state.tableSource],
        source: state => state.tableSource,
        sourceUuid: state => state.tableSourceUuid
      }),
      ...globalMapState(['appliesTo', 'calculateFrom', 'predefinedVendorChargeNames', 'currentBranch',
        'vendorChargeLotStatuses']),
      calculatedAppliesTo() {
        if (this.source == 'inventory') {
          return [
            {
              key: 'Inventory',
              value: 'inventory'
            },
            {
              key: 'Lot',
              value: 'lot'
            }
          ];
        }
        if (this.source == 'lot') {
          return [
            {
              key: 'Lot',
              value: 'lot'
            }
          ];
        }
        return this.appliesTo;
      },
      computedSize() {
        return this.model.data.func_name ? 'sm' : 'md';
      },
      hasPredefinedVendorChargeNames() {
        return this.predefinedVendorChargeNames && this.predefinedVendorChargeNames.length > 1;
      }
    },
    watch: {
      percentageUsed(val) {
        if (!val) {
          this.model.data.is_aggregate = false;
        }
      },
      'model.data.is_taxable': function(val) {
        if (!val) {
          this.model.data.is_tax_inclusive = false;
        }
      },
      'model.data.applies_to': function(val) {
        if (val !== 'lot') {
          for (let tier of this.model.data.tiers) {
            tier.percent = null;
          }
        }
      }
    },
    methods: {
      ...mapActions(['hideModal', 'addOrUpdateVendorCharge', 'updateTableData', 'getTableData']),
      onShow() {
        this.loading = false;
        this.showConfirmation = true;
        if (this.vendorChargeModal.object) {
          this.model = new VendorChargeModel(this.vendorChargeModal.object);
          if (this.hasPredefinedVendorChargeNames) {
            if (this.predefinedVendorChargeNames.find(x => x.value === this.model.name)) {
              this.model.name_dropdown_value = this.model.name;
            } else {
              this.model.name_dropdown_value = 'other';
            }
          }
        } else {
          this.model.data.tax_rate = this.currentBranch.tax_rate;
        }
      },
      onHidden() {
        this.model = new VendorChargeModel();
        this.hideModal();
      },
      handleTierChange(v) {
        this.percentageUsed = v;
        if (!v) {
          this.model.data.calculate_from = [];
        }
      },
      onChargeNameDropdownValueChange() {
        this.model.name = '';
      },
      async onSubmit() {
        if (new Set(this.model.data.tiers.map(x => x.amount_over)).size != this.model.data.tiers.length) {
          this.$errorDialog(this.$t('Please remove tiers with duplicated "amount over" value'));
          return;
        }
        for (let tier of this.model.data.tiers) {
          if (tier.percent === "") {
            tier.percent = null;
          }
          if (tier.flat_price === "") {
            tier.percent = null;
          }
          if (tier.percent === null && tier.flat_price === null) {
            this.$errorDialog(this.$t('Please fill either "Percentage" or "Flat Price" for all tiers'));
            return;
          }
        }
        this.loading = true;
        if (!this.model.data.tax_rate) {
          this.model.data.tax_rate = 0;
        }
        if (this.vendorChargeModal.object && this.vendorChargeModal.object.uuid) {
          this.model['uuid'] = this.vendorChargeModal.object.uuid;
          this.model.data['charge_uuid'] = this.vendorChargeModal.object.uuid;
        }
        this.model[`${this.source}_uuid`] = this.sourceUuid;
        this.showConfirmation = false;
        if (this.model.name_dropdown_value && !this.model.name) {
          this.model.name = this.model.name_dropdown_value;
        }
        if (!this.model.data.calculate_from.length) {
          this.model.data.minimum_calculate_from = 0;
        }
        if (this.model.data.applies_to !== 'lot') {
          this.model.data.lot_statuses = [];
        }
        let response = await VendorChargesService.addOrUpdateVendorCharge(this.model);
        this.loading = false;
        if (response.success) {
          let rows = cloneDeep(this.tableData.rows);
          let message;
          if (this.vendorChargeModal.object) {
            if (this.model.level == response.body.level) {
              message = this.$t('Vendor Charge was successfully updated');
            } else {
              message = this.$t('Vendor Charge was successfully overridden');
            }
            this.getTableData();
          } else {
            rows.push(response.body);
            this.updateTableData(rows);
            message = this.$t('Vendor Charge was successfully created');
          }
          this.$toast('success', message);
          this.showConfirmation = false;
          this.hideModal();
        } else {
          this.showConfirmation = true;
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
              this.model.setMultipleErrors(response.body);
              this.model.data.setMultipleErrors(response.body);
          }
        }
      }
    }
  }
</script>

<style scoped>
  .narrow-modal {
    max-width: 250px;
  }
  .card-header, .card-body {
    padding: 7px;
  }
  .bold {
    font-weight: 700;
  }
</style>
